import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useDebugContext } from '@/hooks/useDebugContext';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { useWorkflowStore } from '@/state/workflow.state';
import { color } from '@/colors';

function DebugRow({ label, value }: { label: string; value: string }) {
  const { copied, copyToClipboard } = useCopyToClipboard({
    trackingName: 'debug_info_copy',
    trackingPayload: { label },
    timeout: 1500,
  });

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Typography variant="label-sm-rg" sx={{ color: color.White64_T, minWidth: 90, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="label-sm-rg" sx={{ color: color.White80_T, wordBreak: 'break-all', flex: 1 }}>
        {value}
      </Typography>
      <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="left">
        <IconButton
          onClick={() => void copyToClipboard(value)}
          size="small"
          sx={{ p: 0.25, color: color.White40_T, flexShrink: 0, '&:hover': { cursor: 'pointer' } }}
        >
          {copied ? <CheckIcon sx={{ fontSize: 11 }} /> : <ContentCopyIcon sx={{ fontSize: 11 }} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export function WorkflowDebugInfo() {
  const { IS_DEBUG } = useDebugContext();
  const owner = useWorkflowStore((state) => state.recipe.owner);
  const workspaceId = useWorkflowStore((state) => state.recipe.workspaceId);

  if (!IS_DEBUG) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'stretch',
        transform: 'translateX(calc(100% - 24px))',
        transition: 'transform 0.25s ease',
        '&:hover': { transform: 'translateX(0)' },
      }}
    >
      {/* Always-visible tab */}
      <Box
        sx={{
          width: 24,
          flexShrink: 0,
          background: color.Black84_T,
          border: `1px solid ${color.White16_T}`,
          borderRight: 'none',
          borderRadius: '4px 0 0 4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 14, color: color.White40_T }} />
      </Box>
      {/* Content panel */}
      <Box
        sx={{
          background: color.Black84_T,
          border: `1px solid ${color.White16_T}`,
          borderRadius: '0 4px 4px 0',
          px: 1.5,
          py: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        <Typography variant="label-xs-rg" sx={{ color: color.White40_T, mb: 0.5 }}>
          DEBUG — WORKFLOW OWNER
        </Typography>
        <DebugRow label="Owner ID" value={owner ?? '—'} />
        <DebugRow label="Workspace ID" value={workspaceId ?? '—'} />
      </Box>
    </Box>
  );
}
